'use client';

import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { getLocalTimeZone, today } from '@internationalized/date';
import { composeTailwindRenderProps } from '@utils';
import {
	DatePicker as AriaDatePicker,
	DatePickerProps as AriaDatePickerProps,
	Button,
	DateValue,
	I18nProvider,
	ValidationResult,
} from 'react-aria-components';

import { Calendar } from '../icons';
import { CalendarDatePicker } from './Calendar';
import { DateInput } from './DateField';
import {
	Description,
	FieldError,
	FieldGroup,
	FieldSuccess,
	FieldVariant,
	FieldWidth,
	iconStyle,
	Label,
} from './Field';
import { Popover } from './Popover';

export interface DatePickerProps<T extends DateValue> extends AriaDatePickerProps<T> {
	label?: string;
	hint?: string;
	hintChildren?: React.ReactNode;
	successMessage?: string;
	errorMessage?: string | ((validation: ValidationResult) => string);
	isValid?: boolean;
	theme?: 'light' | 'dark';
	variant?: FieldVariant;
	width?: FieldWidth;
	onValidationChange?: (isInvalid: boolean, maxValue?: DateValue) => void;
}

export function DatePicker<T extends DateValue>({
	label,
	hint,
	hintChildren,
	successMessage,
	errorMessage,
	isValid,
	theme,
	variant,
	width,
	onValidationChange,
	...props
}: DatePickerProps<T>) {
	const id = useId();
	const prevValueRef = useRef(props.value);
	const [remountKey, setRemountKey] = useState(0);
	const latestValidationRef = useRef<{ isInvalid: boolean; maxValue: T }>({
		isInvalid: false,
		maxValue: (props.maxValue ||
			(today(getLocalTimeZone()).set({ month: 12, day: 31 }) as unknown as T)) as T,
	});
	const prevValidationRef = useRef<{ isInvalid: boolean; maxValue: T } | null>(null);

	// Set default maxValue to current year if not provided
	const defaultMaxValue = useMemo(() => {
		return props.maxValue ? props.maxValue : today(getLocalTimeZone());
	}, [props.maxValue]);

	// Reset validation state when value is cleared
	useEffect(() => {
		if (prevValueRef.current && !props.value) {
			// Value was cleared - force remount and reset validation
			setRemountKey(prev => prev + 1);
			onValidationChange?.(false, defaultMaxValue);
		}
		prevValueRef.current = props.value;
	}, [props.value, onValidationChange, defaultMaxValue]);

	// Call onValidationChange only when validation state actually changes
	useEffect(() => {
		const current = latestValidationRef.current;
		const prev = prevValidationRef.current;

		// Only call if validation state has changed
		if (!prev || prev.isInvalid !== current.isInvalid || prev.maxValue !== current.maxValue) {
			onValidationChange?.(current.isInvalid, current.maxValue);
			prevValidationRef.current = { ...current };
		}
	});

	return (
		<I18nProvider locale="en-AU">
			<AriaDatePicker
				key={remountKey}
				id={id}
				shouldForceLeadingZeros
				{...props}
				maxValue={defaultMaxValue as T}
				className={composeTailwindRenderProps(props.className, 'group flex flex-col gap-2')}
			>
				{({ isInvalid, isDisabled, isFocusWithin }) => {
					// Store validation state in ref for useEffect to pick up
					latestValidationRef.current = { isInvalid, maxValue: defaultMaxValue as T };
					return (
						<>
							{label && <Label isRequired={props.isRequired}>{label}</Label>}
							{hint && <Description>{hint}</Description>}
							{hintChildren && hintChildren}
							{errorMessage && <FieldError>{errorMessage}</FieldError>}
							{isValid && successMessage && <FieldSuccess>{successMessage}</FieldSuccess>}
							<FieldGroup variant={variant} width={width} theme={theme}>
								<DateInput className="min-w-36 flex-1 text-sm" />
								<Button className="cursor-pointer rounded-xs p-1 outline-offset-0">
									<Calendar
										className={iconStyle({
											theme,
											isInvalid,
											isDisabled,
											isValid,
											isFocusWithin,
										})}
									/>
								</Button>
							</FieldGroup>
							<Popover className="w-fit min-w-fit p-3">
								<CalendarDatePicker
									maxValue={props.maxValue || defaultMaxValue}
									minValue={props.minValue}
								/>
							</Popover>
						</>
					);
				}}
			</AriaDatePicker>
		</I18nProvider>
	);
}
